import React, {FC, useCallback, useMemo, useEffect, useLayoutEffect, useState, Fragment} from "react";
// antd:
import { Layout } from "antd";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../libs/redux-hooks.ts";
// Reducers:
import { reloadConfigAndApps, getAllApps, getDesktopApps, getConfig, getDict, getCompact, setDesktopApps, getDesktopFixed, getShowDesktopIconsTitle } from "../libs/reducer.ts";
// Components:
import { LayoutMainToolbar, OneDesktopIcon, OneDesktopWidget, AppIcons } from "../components/react/index";
// import DivE from "../components/react/DivE.tsx";
import ReactGridLayout from "../components/react/ReactGridLayout.tsx";
// Types
import type {LayoutType} from "../components/react/ReactGridLayout.tsx";

import fnc from "../libs/fnc.ts";

/* const useDimensions = (elementRef) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = elementRef?.current;
    if (el) { setDimensions({ width: el.clientWidth, height: el.clientHeight }) }
  }, [elementRef]);
  return [dimensions];
}; */

const LoadingSpinner = (<div className="uk-flex uk-flex-center uk-flex-middle" style={{height: "100%"}}><div className="loader02"></div></div>);

const stlBgImgError:{[key:string]:string} = {
  backgroundImage: "url('/img/app/bg/error.png')",
  backgroundColor: "rgba(70,70,70,100)", backgroundBlendMode: "multiply", OBackgroundSize: "cover", MozBackgroundSize: "cover", WebkitBackgroundSize: "cover", backgroundPosition: "center center"
};

const ErrorContent:FC = (msg:string) => (
  <div className="uk-flex uk-flex-center uk-flex-middle" style={{height: "100%"}}>
    <h2 className="uk-margin-remove uk-text-center txt-white txt-stroke-red-shadow">{msg}</h2>
  </div>
);

const {rz} = window;

const HomePage:FC = () => {
  const dispatch = useAppDispatch();
  const desktopApps = useAppSelector(getDesktopApps);
  const allApps = useAppSelector(getAllApps);
  const cfg = useAppSelector(getConfig);
  const dct = useAppSelector(getDict);
  const compact = useAppSelector(getCompact);
  const isDesktopFixed = useAppSelector(getDesktopFixed);
  const showDesktopIconsTitle = useAppSelector(getShowDesktopIconsTitle);

  // useEffect(() => { console.log("Desktop", {isDesktopFixed}) }, [isDesktopFixed]);

  // Count Icons-app in one column for variant desktop types
  const countIconsInOneColumn = useMemo(() => ({lg: 11, md: 10, sm: 8, xs: 6, xxs: 4}), []);
  // eslint-disable-next-line max-len
  const ilyl = useMemo(() => ({x: 0, y: 0, w: 1, h: (showDesktopIconsTitle ? 2 : 1), minW: 1, maxW: 1, minH: (showDesktopIconsTitle ? 2 : 1), maxH: (showDesktopIconsTitle ? 2 : 1), static: false, isDraggable: true, isResizable: false, isBounded: true}), [showDesktopIconsTitle]);
  const wlyl = useMemo(() => ({x: 0, y: 0, w: 4, h: 4, minW: 1, maxW: 24, minH: 1, maxH: 24, static: false, isDraggable: true, isResizable: true, isBounded: true}), []);

  const [toolbarHeight, setToolbarHeight] = useState(compact ? 40 : 55);
  useEffect(() => { setToolbarHeight(compact ? 40 : 55) }, [compact]);

  const widgetsApps = useMemo(() => (desktopApps.filter((app) => (app.type === "widget"))), [desktopApps]);

  // ReDraw Desktop-container if User changes Screen-size:
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [dummyValue, setRedrawTimeStamp] = useState(() => ((new Date().getTime())));
  useLayoutEffect(() => { // On document was changed size
    function updateWindowSize() { setRedrawTimeStamp((new Date().getTime())) }
    window.addEventListener("resize", updateWindowSize);
    // Update desktop periodically
    const intId = setInterval(() => { setRedrawTimeStamp((new Date().getTime())) }, 1000);
    // When component is deactivated
    return () => { window.removeEventListener("resize", updateWindowSize); clearInterval(intId) }
  }, []);

  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [errorMessage, setErrorMessage] = useState("");
  const [layouts, setLayuot] = useState<LayoutType>({lg: [] });

  // =========== Component did mount ===========
  useEffect(() => {
    dispatch(reloadConfigAndApps()).then(() => { setLoading(false) });
  }, [dispatch]);

  const stl1 = useMemo<{[key:string]:any}>(() => ({
    backgroundImage: "url('" + (cfg?.personalsettings?.find((rec) => (rec.key === "desktopBgImage"))?.value || "") + "')",
    backgroundColor: (isDesktopFixed ? "rgba(70,70,70,100)" : "rgba(40,40,40,100)"), backgroundBlendMode: "multiply",
    OBackgroundSize: "cover", MozBackgroundSize: "cover", WebkitBackgroundSize: "cover", backgroundPosition: "center center"
  }), [cfg?.personalsettings, isDesktopFixed]);

  const gridApps = useMemo<Array<React.ReactNode>>(() => {
    const apps:Array<React.ReactNode> = [];
    const louts:LayoutType = {lg: [], md: [], sm: [], xs: [], xxs: []};

    let k = 0;
    (desktopApps || []).filter((app) => (app.type !== "widget")).forEach((app) => {
      const lg = {...ilyl, i: app.key, x: app?.showOnDesktop?.lg?.x ?? Math.floor(k / countIconsInOneColumn.lg), y: app?.showOnDesktop?.lg?.y ?? (k % countIconsInOneColumn.lg), isDraggable: !isDesktopFixed};
      const md = {...ilyl, i: app.key, x: app?.showOnDesktop?.md?.x ?? Math.floor(k / countIconsInOneColumn.md), y: app?.showOnDesktop?.md?.y ?? (k % countIconsInOneColumn.md), isDraggable: !isDesktopFixed};
      const sm = {...ilyl, i: app.key, x: app?.showOnDesktop?.sm?.x ?? Math.floor(k / countIconsInOneColumn.sm), y: app?.showOnDesktop?.sm?.y ?? (k % countIconsInOneColumn.sm), isDraggable: !isDesktopFixed};
      const xs = {...ilyl, i: app.key, x: app?.showOnDesktop?.xs?.x ?? Math.floor(k / countIconsInOneColumn.xs), y: app?.showOnDesktop?.xs?.y ?? (k % countIconsInOneColumn.xs), isDraggable: !isDesktopFixed};
      const xxs = {...ilyl, i: app.key, x: app?.showOnDesktop?.xxs?.x ?? Math.floor(k / countIconsInOneColumn.xxs), y: app?.showOnDesktop?.xxs?.y ?? (k % countIconsInOneColumn.xxs), isDraggable: !isDesktopFixed};
      // @ts-ignore
      louts.lg.push(lg); louts.md.push(md); louts.sm.push(sm); louts.xs.push(xs); louts.xxs.push(xxs);
      apps.push(
        <div key={app.key}>
          <OneDesktopIcon app={app} />
        </div>
      );
      k++;
    });

    (widgetsApps || []).forEach((app) => {
      // eslint-disable-next-line max-len
      const lg = {...wlyl, i: app.key, w: app?.showOnDesktop?.lg?.w ?? 4, h: app?.showOnDesktop?.lg?.h ?? 4, x: app?.showOnDesktop?.lg?.x ?? Math.floor(k / countIconsInOneColumn.lg), y: app?.showOnDesktop?.lg?.y ?? (k % countIconsInOneColumn.lg), isDraggable: !isDesktopFixed, isResizable: !isDesktopFixed};
      // eslint-disable-next-line max-len
      const md = {...wlyl, i: app.key, w: app?.showOnDesktop?.md?.w ?? 4, h: app?.showOnDesktop?.md?.h ?? 4, x: app?.showOnDesktop?.md?.x ?? Math.floor(k / countIconsInOneColumn.md), y: app?.showOnDesktop?.md?.y ?? (k % countIconsInOneColumn.md), isDraggable: !isDesktopFixed, isResizable: !isDesktopFixed};
      // eslint-disable-next-line max-len
      const sm = {...wlyl, i: app.key, w: app?.showOnDesktop?.sm?.w ?? 4, h: app?.showOnDesktop?.sm?.h ?? 4, x: app?.showOnDesktop?.sm?.x ?? Math.floor(k / countIconsInOneColumn.sm), y: app?.showOnDesktop?.sm?.y ?? (k % countIconsInOneColumn.sm), isDraggable: !isDesktopFixed, isResizable: !isDesktopFixed};
      // eslint-disable-next-line max-len
      const xs = {...wlyl, i: app.key, w: app?.showOnDesktop?.xs?.w ?? 2, h: app?.showOnDesktop?.xs?.h ?? 2, x: app?.showOnDesktop?.xs?.x ?? Math.floor(k / countIconsInOneColumn.xs), y: app?.showOnDesktop?.xs?.y ?? (k % countIconsInOneColumn.xs), isDraggable: !isDesktopFixed, isResizable: !isDesktopFixed};
      // eslint-disable-next-line max-len
      const xxs = {...wlyl, i: app.key, w: app?.showOnDesktop?.xxs?.w ?? 2, h: app?.showOnDesktop?.xxs?.h ?? 2, x: app?.showOnDesktop?.xxs?.x ?? Math.floor(k / countIconsInOneColumn.xxs), y: app?.showOnDesktop?.xxs?.y ?? (k % countIconsInOneColumn.xxs), isDraggable: !isDesktopFixed, isResizable: !isDesktopFixed};
      // @ts-ignore
      louts.lg.push(lg); louts.md.push(md); louts.sm.push(sm); louts.xs.push(xs); louts.xxs.push(xxs);
      apps.push(
        <div key={app.key} id={app.key} >
          <OneDesktopWidget key={"widget-" + app.key} wapp={app} />
        </div>
      );
      k++;
    });

    if (!apps.length) {
      const app = allApps.find((ap) => (ap.key === "portalhelp"));
      if (app) {
        apps.push(
          <div key={app.key}>
            <OneDesktopIcon app={app} />
          </div>
        );
        const lyot = {...ilyl, i: app.key, isDraggable: !isDesktopFixed };
        // @ts-ignore
        louts.lg.push(lyot); louts.md.push(lyot); louts.sm.push(lyot); louts.xs.push(lyot); louts.xxs.push(lyot);
      }
    }
    setLayuot({lg: louts.lg, md: louts.md, sm: louts.sm, xs: louts.xs, xxs: louts.xxs});
    return apps;
  }, [allApps, desktopApps, widgetsApps, isDesktopFixed, countIconsInOneColumn, ilyl, wlyl]);

  const onLayoutChange = useCallback((layout, lyouts) => {
    if (cfg.isDev) { console.log({_: "onLayoutChange", layout, layouts: lyouts}) }
    fnc.desktopChangeLayout(lyouts).then((ans) => {
      dispatch(setDesktopApps(ans));
      // if (cfg.isDev) { console.log(ans) }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.isDev, dispatch]);

  const onBreakpointChange = useCallback((breakpoint) => {
    if (cfg.isDev) { console.log({_: "onBreakpointChange", breakpoint}) }
  }, [cfg.isDev]);

  const onDragStop = useCallback((layout, oldItem, newItem, placeholder, event, element) => {
    if (cfg.isDev) { console.log({_: "onDragStop", layout, oldItem, newItem, placeholder, event, element}) }
  }, [cfg.isDev]);

  const pageSkelet = (elmns:any) => (
    <Layout id="antd-layout" className="layout">
      <div id="background-image" style={errorMessage ? stlBgImgError : stl1}>
        <LayoutMainToolbar
          pageTitle={rz.switchCase([cfg?.ad?.usersLoginEndsWith === "@smartteam.dev", dct.p.homepageTitleGC, cfg?.ad?.usersLoginEndsWith === "@gescons.ru", dct.p.homepageTitleGES, "PORTAL"])}
            pageIcon={
              <AppIcons
                icopath={rz.switchCase([cfg?.ad?.usersLoginEndsWith === "@smartteam.dev", dct.p.homepageIcon?.icopathGC, cfg?.ad?.usersLoginEndsWith === "@gescons.ru", dct.p.homepageIcon?.icopathGES, "fc/FcGlobe"])}
                attrs={{
                  color: rz.switchCase([cfg?.ad?.usersLoginEndsWith === "@smartteam.dev", dct.p.homepageIcon?.colorGC, cfg?.ad?.usersLoginEndsWith === "@gescons.ru", dct.p.homepageIcon?.colorGES, "black|white"]),
                  size: "1.8em", className: dct.p.homepageIcon?.className || "", tooltip: dct.p.homepageTitle
                }}
              />
            }
         />
        <div key="virtual-desktop" id="virtual-desktop" className="uk-container-expand uk-position-relative"
          style={{
            // @ts-ignore
            height: (window.innerHeight) - toolbarHeight, marginTop: toolbarHeight,
            width: "100%", padding: "10px 10px", overflow: "auto"
          }}
        >
          {elmns}
        </div>
      </div>
    </Layout>
  );

  if (loading) return pageSkelet(LoadingSpinner);
  if (errorMessage) return pageSkelet(ErrorContent(errorMessage));

  return pageSkelet(
    <Fragment>
      <ReactGridLayout id="react-grid-layout" className="layout"
        compactType={null} allowOverlap={false}
        layouts={layouts} onLayoutChange={onLayoutChange} onBreakpointChange={onBreakpointChange} onDragStop={onDragStop}
        style={{
          // @ts-ignore
          height: (window.innerHeight) - toolbarHeight - 20, width: "100%"
        }}
      >
          {(gridApps && gridApps.length && gridApps) || []}
      </ReactGridLayout>
    </Fragment>
  );
};

export default HomePage;
